import React, { useMemo, useState } from 'react';
import Dialog from './Dialog';
import './OptionPackageList.css';
import { API_URLS } from './config';
import fetchWithAuth from './api';

const getPackageMetadata = (pkg) => {
  let packageData = {};

  try {
    packageData = pkg.package ? JSON.parse(pkg.package) : {};
  } catch {
    packageData = {};
  }

  return {
    name: packageData.PackageTitle || packageData.PackageName || pkg.rowKey || '',
    symbol: packageData.Symbol || '',
    createdOn: pkg.createdOn || pkg.createdDate || pkg.createdAt || '',
  };
};

const formatCreatedDate = (createdOn) => {
  if (!createdOn) {
    return '';
  }

  const createdDate = new Date(createdOn);
  if (Number.isNaN(createdDate.getTime())) {
    return '';
  }

  return createdDate.toLocaleDateString();
};

const OptionPackageList = ({ onSelectPackage, user, packages, isLoading, error, onRefresh, handleNewPackage, userPermissions }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const sortedPackages = useMemo(() => {
    const getSortValue = (pkg) => {
      const metadata = getPackageMetadata(pkg);

      if (sortConfig.key === 'createdOn') {
        const timestamp = new Date(metadata.createdOn).getTime();
        return Number.isNaN(timestamp) ? 0 : timestamp;
      }

      return metadata[sortConfig.key].toUpperCase();
    };

    return [...packages].sort((firstPackage, secondPackage) => {
      const firstValue = getSortValue(firstPackage);
      const secondValue = getSortValue(secondPackage);

      if (firstValue < secondValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }

      if (firstValue > secondValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }

      return 0;
    });
  }, [packages, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((currentSort) => ({
      key,
      direction: currentSort.key === key && currentSort.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) {
      return '';
    }

    return sortConfig.direction === 'asc' ? ' ^' : ' v';
  };

  const handleDelete = (rowKey) => {
    setPackageToDelete(rowKey);
    setIsDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (packageToDelete) {
      try {
        const response = await fetchWithAuth(`${API_URLS.DeleteOptionPackage}/${packageToDelete}`, user, userPermissions, {
          method: 'DELETE',
        });

        if (response.ok) {
          onRefresh();
        } else {
          console.error('Failed to delete package');
        }
      } catch (error) {
        console.error('Error deleting package:', error);
      }
      setIsDialogOpen(false);
      setPackageToDelete(null);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="option-package-list">
      <Dialog
        isOpen={isDialogOpen}
        title="Delete Package"
        message={`Are you sure you want to delete the package "${packageToDelete}"?`}
        onConfirm={confirmDelete}
        onCancel={() => {
          setIsDialogOpen(false);
          setPackageToDelete(null);
        }}
      />
      <div className="option-package-list-header">
        <h2 className="text-2xl font-bold mb-4">Option Packages</h2>
      </div>
      <div className="package-table-wrap">
        <table className="package-table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => handleSort('name')}>
                  Package Name{getSortIndicator('name')}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => handleSort('symbol')}>
                  Symbol{getSortIndicator('symbol')}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => handleSort('createdOn')}>
                  Created Date{getSortIndicator('createdOn')}
                </button>
              </th>
              <th className="package-actions-heading">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedPackages.map((pkg) => {
              const metadata = getPackageMetadata(pkg);

              return (
                <tr key={pkg.rowKey}>
                  <td>
                    <button type="button" className="package-name-btn" onClick={() => onSelectPackage(pkg)}>
                      {metadata.name}
                    </button>
                  </td>
                  <td>{metadata.symbol}</td>
                  <td>{formatCreatedDate(metadata.createdOn)}</td>
                  <td className="package-actions">
                    <button type="button" onClick={() => handleDelete(pkg.rowKey)} className="delete-btn">
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OptionPackageList;
